import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, FileOutput, GitBranch, Layers, Plus, RotateCcw,
  Save, Search, Send, Trash2, Undo2, X, XCircle,
} from 'lucide-react';
import * as api from '../../api';
import { Btn, DatePicker, Input, Modal, RNLLoader, SearchableSelect, Select, Textarea, useToast } from '../UI';
import { CommentModal, Field, LifecycleBar, LineStatusPill, Panel, StatTile, TakeoffStatusPill } from './MTOShared';
import {
  EDITABLE, LINE_STATUS, LINE_TYPES, NAVY, SYSTEM_SUGGESTIONS, blankLine, blankMaterial, engineeringQuantity, fmt, fmtDate,
  materialQuantities, lineStatus, num, roleNames, toPayloadSystems, toWorking, validateSystems,
} from './mtoUtils';

const HEADER_FIELDS = ['drawing_reference', 'drawing_revision', 'boq_reference', 'location', 'date_prepared', 'remarks'];
const NEW_MATERIAL = '__new__';

// compact cell input for the breakdown grid
const Cell = ({ value, onChange, type = 'text', width = 90, align = 'left', placeholder, readOnly }) => (
  <input value={value ?? ''} type={type} placeholder={placeholder} readOnly={readOnly}
         onChange={e => onChange(e.target.value)} step={type === 'number' ? 'any' : undefined} min={type === 'number' ? 0 : undefined}
         style={{
           width, padding: '6px 8px', fontSize: 12, borderRadius: 7, textAlign: align, fontFamily: 'var(--font)',
           border: '1px solid var(--border-light)', background: readOnly ? 'transparent' : 'var(--bg-base)', color: 'var(--text-primary)',
         }} />
);

const th = { padding: '8px 8px', fontSize: 9.5, fontWeight: 800, color: '#fff', background: NAVY, border: '1px solid black', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', textAlign: 'left' };
const td = { padding: '6px 8px', fontSize: 12, border: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-primary)' };

export default function MTOEditor({ takeoffId, onBack, onOpenTakeoff, onOpenMR }) {
  const toast = useToast();
  const [takeoff, setTakeoff] = useState(null);
  const [header, setHeader] = useState({});
  const [systems, setSystems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [materials, setMaterials] = useState([]);
  const [me, setMe] = useState(null);
  const [canAct, setCanAct] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [busy, setBusy] = useState('');
  const [modal, setModal] = useState(null);   // 'system' | 'submit' | 'approve' | 'return' | 'reject' | 'revise' | 'mr' | 'delete' | 'leave'
  const [loadError, setLoadError] = useState('');

  // ── load ──────────────────────────────────────────────────────────────
  const adopt = useCallback((t) => {
    setTakeoff(t);
    setHeader(Object.fromEntries(HEADER_FIELDS.map(k => [k, t[k] ?? ''])));
    const working = toWorking(t);
    setSystems(working);
    setActiveKey(k => (working.some(s => s._key === k) ? k : working[0]?._key ?? null));
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      api.clearApiCache();
      const t = await api.getTakeoff(takeoffId);
      adopt(t);
      // expand every line on first load of a small take-off; big ones start collapsed
      const lines = (t.systems || []).flatMap(s => s.boq_lines);
      setExpanded(lines.length <= 12 ? Object.fromEntries(lines.map(l => [l.id, true])) : {});
      if (t.workflow_instance_id) {
        api.getWorkflowTracking(t.workflow_instance_id).then(setTracking).catch(() => setTracking(null));
        if (t.status === 'pending_approval') {
          api.getMyInbox().then(r => setCanAct((r || []).some(i => i.entity_id === t.id))).catch(() => setCanAct(false));
        } else setCanAct(false);
      } else { setTracking(null); setCanAct(false); }
    } catch (e) {
      setLoadError(e.message || 'Could not open this take-off.');
    }
  }, [takeoffId, adopt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
    api.listMaterials(0, 1000).then(r => {
      const list = Array.isArray(r) ? r : (r?.items || r?.data || []);
      setMaterials(list.filter(m => m.is_active !== false));
    }).catch(() => {});
  }, []);

  // warn before leaving the page with unsaved edits
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // ── permissions ───────────────────────────────────────────────────────
  const isAdmin = roleNames(me).includes('super_admin');
  const isPreparer = !!me && !!takeoff && (me.user_id || me.id) === takeoff.prepared_by_id;
  const editable = !!takeoff && EDITABLE.includes(takeoff.status) && (isPreparer || isAdmin);

  // ── working-copy edits ────────────────────────────────────────────────
  const touch = () => setDirty(true);
  const setH = (k) => (v) => { setHeader(h => ({ ...h, [k]: v })); touch(); };
  const updSystem = (key, fn) => { setSystems(ss => ss.map(s => (s._key === key ? fn(s) : s))); touch(); };
  const updLine = (sKey, lKey, patch) => updSystem(sKey, s => ({ ...s, boq_lines: s.boq_lines.map(l => (l._key === lKey ? { ...l, ...patch } : l)) }));
  const updMat = (sKey, lKey, mKey, patch) => updSystem(sKey, s => ({
    ...s, boq_lines: s.boq_lines.map(l => (l._key !== lKey ? l : { ...l, materials: l.materials.map(m => (m._key === mKey ? { ...m, ...patch } : m)) })),
  }));
  const addMat = (sKey, lKey) => {
    const m = blankMaterial();
    const line = systems.find(s => s._key === sKey)?.boq_lines.find(l => l._key === lKey);
    if (line && line.line_type !== 'lot') { m.measured_uom = line.boq_uom || ''; m.purchase_uom = line.boq_uom || ''; }
    updSystem(sKey, s => ({ ...s, boq_lines: s.boq_lines.map(l => (l._key === lKey ? { ...l, materials: [...l.materials, m] } : l)) }));
    setExpanded(x => ({ ...x, [lKey]: true }));
  };
  const removeMat = (sKey, lKey, mKey) => updSystem(sKey, s => ({
    ...s, boq_lines: s.boq_lines.map(l => (l._key === lKey ? { ...l, materials: l.materials.filter(m => m._key !== mKey) } : l)),
  }));
  const addLine = (sKey) => {
    const l = blankLine();
    updSystem(sKey, s => ({ ...s, boq_lines: [...s.boq_lines, l] }));
    setExpanded(x => ({ ...x, [l._key]: true }));
  };
  const removeLine = (sKey, lKey) => updSystem(sKey, s => ({ ...s, boq_lines: s.boq_lines.filter(l => l._key !== lKey) }));
  const removeSystem = (sKey) => {
    setSystems(ss => ss.filter(s => s._key !== sKey));
    setActiveKey(k => (k === sKey ? systems.find(s => s._key !== sKey)?._key ?? null : k));
    touch();
  };

  const pickMaterial = (sKey, lKey, mKey, value) => {
    if (value === NEW_MATERIAL) {
      updMat(sKey, lKey, mKey, { material_id: null, material_code: '', is_new_material: true });
      return;
    }
    const mm = materials.find(x => x.material_id === value);
    if (!mm) return;
    updMat(sKey, lKey, mKey, {
      material_id: mm.material_id, material_code: mm.code || '', description: mm.name || mm.description || '',
      specification: mm.description && mm.description !== mm.name ? mm.description : '', is_new_material: false,
      measured_uom: mm.unit_of_measure || '', purchase_uom: mm.unit_of_measure || '',
    });
  };

  const materialOptions = useMemo(() => [
    { value: NEW_MATERIAL, label: '➕ New material (not in Material Master)' },
    ...materials.map(m => ({ value: m.material_id, label: `${m.code ? `${m.code} · ` : ''}${m.name}${m.unit_of_measure ? ` (${m.unit_of_measure})` : ''}` })),
  ], [materials]);

  // ── live figures (the server recomputes them on save) ─────────────────
  const stats = useMemo(() => {
    const st = { systems: systems.length, lines: 0, complete: 0, partial: 0, notStarted: 0, mats: 0, newMats: 0, above: 0, proc: 0 };
    systems.forEach(s => s.boq_lines.forEach(l => {
      const ls = lineStatus(l);
      const eng = engineeringQuantity(l);
      st.lines += 1;
      st.complete += ls === 'complete';
      st.partial += ls === 'partial';
      st.notStarted += ls === 'not_started' || ls === 'requires_breakdown';
      st.mats += l.materials.length;
      st.newMats += l.materials.filter(m => m.is_new_material && !m.material_id).length;
      st.above += eng !== null && num(l.boq_quantity) > 0 && eng > num(l.boq_quantity);
      st.proc += l.materials.reduce((a, m) => a + materialQuantities(m).procurement, 0);
    }));
    return st;
  }, [systems]);

  const active = systems.find(s => s._key === activeKey) || null;
  const shownLines = useMemo(() => {
    if (!active) return [];
    const q = search.trim().toLowerCase();
    return active.boq_lines.filter(l => {
      if (filter && lineStatus(l) !== filter) return false;
      if (!q) return true;
      const hay = [l.boq_code, l.boq_description, l.boq_uom, ...l.materials.flatMap(m => [m.description, m.material_code, m.specification, m.brand])].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [active, filter, search]);

  // ── server actions ────────────────────────────────────────────────────
  const save = async ({ quiet } = {}) => {
    const problem = validateSystems(systems);
    if (problem) { toast.show(problem, 'error'); return false; }
    setBusy('save');
    try {
      const payload = { ...Object.fromEntries(HEADER_FIELDS.map(k => [k, header[k] === '' ? null : header[k]])), systems: toPayloadSystems(systems) };
      const t = await api.saveTakeoff(takeoff.id, payload);
      adopt(t);
      // keep the same lines open after the ids change on save
      setExpanded(x => {
        const next = {};
        (t.systems || []).forEach((s, si) => s.boq_lines.forEach((l, li) => {
          const before = systems[si]?.boq_lines[li];
          if (before && x[before._key]) next[l.id] = true;
        }));
        return next;
      });
      if (!quiet) toast.show(`${t.takeoff_ref} saved.`, 'success');
      return true;
    } catch (e) {
      toast.show(e.message || 'Could not save the take-off.', 'error');
      return false;
    } finally {
      setBusy('');
    }
  };

  const run = async (key, fn, after) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r?.message) toast.show(r.message, 'success');
      setModal(null);
      if (after) after(r); else await load();
    } catch (e) {
      toast.show(e.message || 'That did not work.', 'error');
    } finally {
      setBusy('');
    }
  };

  const submit = async (comment) => {
    if (dirty && !(await save({ quiet: true }))) return;
    await run('submit', () => api.submitTakeoff(takeoff.id, comment));
  };

  const goBack = () => (dirty ? setModal('leave') : onBack());

  if (loadError) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <AlertTriangle size={28} color="#dc2626" />
        <p style={{ fontSize: 14, margin: '10px 0 16px' }}>{loadError}</p>
        <Btn variant="secondary" onClick={onBack}><ArrowLeft size={14} /> Back</Btn>
      </div>
    );
  }
  if (!takeoff) return <RNLLoader title="Opening take-off..." subtitle="Loading the BOQ breakdown" />;

  const neverSubmitted = !takeoff.workflow_instance_id;
  const linesWithBalance = takeoff.systems.flatMap(s => s.boq_lines.map(l => ({ ...l, system_name: s.system_name })))
    .filter(l => l.materials.length && (l.boq_quantity == null || num(l.balance) > 0));

  return (
    <div style={{ paddingBottom: 70 }}>
      {/* ── title bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 800, color: NAVY }}>{takeoff.takeoff_ref}</span>
          <span style={{ fontSize: 12, fontWeight: 800, padding: '3px 9px', borderRadius: 7, background: 'var(--bg-base)', border: '1px solid var(--border-light)' }}>{takeoff.revision}</span>
          <TakeoffStatusPill status={takeoff.status} />
          {takeoff.currently_with && takeoff.status === 'pending_approval' && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>with <b>{takeoff.currently_with}</b></span>}
          {dirty && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#b45309' }}>● Unsaved changes</span>}
        </div>
        <Btn variant="secondary" onClick={goBack}><ArrowLeft size={14} /> Back to Register</Btn>
      </div>

      {takeoff.revision_reason && (
        <div style={{ padding: '9px 14px', borderRadius: 10, background: '#ede9fe', color: '#4c1d95', fontSize: 13, marginBottom: 14 }}>
          <b>{takeoff.revision}</b> — revision reason: {takeoff.revision_reason}
        </div>
      )}
      {takeoff.status === 'returned' && tracking?.return_reason && (
        <div style={{ padding: '9px 14px', borderRadius: 10, background: '#ffedd5', color: '#9a3412', fontSize: 13, marginBottom: 14 }}>
          <b>Returned for correction:</b> {tracking.return_reason}
        </div>
      )}

      {/* ── header ── */}
      <Panel title="Take-off header">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
          <Field label="Project">{takeoff.project_code} — {takeoff.project_name}</Field>
          <Field label="Building / Zone">{takeoff.building_zone}</Field>
          <Field label="Discipline">{takeoff.discipline_name}</Field>
          <Field label="Primary System">{takeoff.primary_system}</Field>
          <Field label="Prepared By">{takeoff.prepared_by_name}</Field>
          <Field label="Submitted">{takeoff.submitted_at ? `${fmtDate(takeoff.submitted_at)}${takeoff.submitted_by_name ? ` · ${takeoff.submitted_by_name}` : ''}` : 'Not yet'}</Field>
          {!editable && <>
            <Field label="Drawing Reference">{takeoff.drawing_reference}</Field>
            <Field label="Drawing Revision">{takeoff.drawing_revision}</Field>
            <Field label="BOQ Reference">{takeoff.boq_reference}</Field>
            <Field label="Location">{takeoff.location}</Field>
            <Field label="Date Prepared">{fmtDate(takeoff.date_prepared)}</Field>
            <Field label="Remarks">{takeoff.remarks}</Field>
          </>}
        </div>
        {editable && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 12 }}>
            <Input label="Drawing Reference" value={header.drawing_reference} onChange={setH('drawing_reference')} />
            <Input label="Drawing Revision" value={header.drawing_revision} onChange={setH('drawing_revision')} />
            <Input label="BOQ Reference" value={header.boq_reference} onChange={setH('boq_reference')} />
            <Input label="Location" value={header.location} onChange={setH('location')} />
            <DatePicker label="Date Prepared" value={header.date_prepared} onChange={setH('date_prepared')} />
            <Input label="Remarks" value={header.remarks} onChange={setH('remarks')} />
          </div>
        )}
      </Panel>

      <Panel title="Material lifecycle (traceability)">
        <LifecycleBar status={takeoff.status} hasMaterialRequest={takeoff.material_request_ids.length > 0} />
      </Panel>

      {/* ── breakdown ── */}
      <Panel title="BOQ requirement & material breakdown"
             right={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>BOQ quantity and unit are read-only (from the BOQ). Totals: required × (1 + add %) · procurement = total ÷ conversion, rounded up.</span>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(105px, 1fr))', gap: 8, marginBottom: 14 }}>
          <StatTile label="Systems" value={stats.systems} />
          <StatTile label="BOQ Lines" value={stats.lines} />
          <StatTile label="Completed" value={stats.complete} accent="#16a34a" onClick={() => setFilter(f => (f === 'complete' ? '' : 'complete'))} active={filter === 'complete'} />
          <StatTile label="Partial" value={stats.partial} accent="#ca8a04" onClick={() => setFilter(f => (f === 'partial' ? '' : 'partial'))} active={filter === 'partial'} />
          <StatTile label="Not Started" value={stats.notStarted} accent="#64748b" onClick={() => setFilter(f => (f === 'not_started' ? '' : 'not_started'))} active={filter === 'not_started'} />
          <StatTile label="Material Lines" value={stats.mats} accent="#7c3aed" />
          <StatTile label="New Materials" value={stats.newMats} accent="#db2777" onClick={() => setFilter(f => (f === 'new_material' ? '' : 'new_material'))} active={filter === 'new_material'} />
          <StatTile label="Eng Above BOQ" value={stats.above} accent="#dc2626" />
          <StatTile label="Procurement Qty" value={fmt(stats.proc, 0)} accent="#0891b2" />
        </div>

        {/* system tabs */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {systems.map(s => {
            const count = s.boq_lines.reduce((a, l) => a + l.materials.length, 0);
            const on = s._key === activeKey;
            return (
              <div key={s._key} style={{ display: 'flex', alignItems: 'center', borderRadius: 9, border: `1px solid ${on ? NAVY : 'black'}`, background: on ? NAVY : 'var(--bg-card)', color: on ? '#fff' : 'var(--text-primary)' }}>
                <button onClick={() => setActiveKey(s._key)} style={{ background: 'none', border: 'none', color: 'inherit', padding: '7px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font)' }}>
                  <Layers size={14} /> {s.system_name} <span style={{ fontWeight: 500, opacity: 0.8 }}>({count} material{count === 1 ? '' : 's'})</span>
                </button>
                {editable && systems.length > 1 && (
                  <button title="Remove this system" onClick={() => removeSystem(s._key)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 8px 0 0', opacity: 0.75, display: 'flex' }}><X size={14} /></button>
                )}
              </div>
            );
          })}
          {editable && <Btn variant="secondary" onClick={() => setModal('system')} style={{ padding: '6px 12px' }}><Plus size={14} /> Add System</Btn>}
        </div>

        {!active ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            No systems yet. {editable ? 'Use Add System to load a BOQ into the working area.' : ''}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ position: 'relative', flex: '1 1 260px' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search BOQ / description / material / spec / UOM…"
                       style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 9, border: '1px solid var(--border-light)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font)' }} />
              </div>
              <div style={{ width: 200 }}>
                <Select title="Filter BOQ lines" value={filter} onChange={setFilter}
                        options={[{ value: '', label: 'All line statuses' }, ...Object.entries(LINE_STATUS).map(([value, m]) => ({ value, label: m.label }))]} />
              </div>
              <Btn variant="secondary" style={{ padding: '7px 11px' }} onClick={() => setExpanded(Object.fromEntries(active.boq_lines.map(l => [l._key, true])))}><ChevronDown size={14} /> Expand all</Btn>
              <Btn variant="secondary" style={{ padding: '7px 11px' }} onClick={() => setExpanded({})}><ChevronRight size={14} /> Collapse all</Btn>
              {editable && <Btn variant="secondary" style={{ padding: '7px 11px' }} onClick={() => addLine(active._key)}><Plus size={14} /> Add BOQ Line</Btn>}
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1250 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 28 }} />
                    <th style={th}>BOQ Code</th>
                    <th style={{ ...th, minWidth: 260 }}>BOQ Description</th>
                    <th style={{ ...th, textAlign: 'right' }}>BOQ Qty</th>
                    <th style={th}>BOQ UOM</th>
                    <th style={{ ...th, textAlign: 'right' }}>Transferred</th>
                    <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                    <th style={th}>Eng vs BOQ</th>
                    <th style={th}>Line Type</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, width: 110 }} />
                  </tr>
                </thead>
                <tbody>
                  {shownLines.length === 0 && (
                    <tr><td colSpan={11} style={{ ...td, textAlign: 'center', padding: 26, color: 'var(--text-secondary)' }}>
                      {active.boq_lines.length ? 'No BOQ lines match this filter.' : 'This system has no BOQ lines yet.'}
                    </td></tr>
                  )}
                  {shownLines.map(l => {
                    const open = !!expanded[l._key];
                    const st = lineStatus(l);
                    const eng = engineeringQuantity(l);
                    const variance = eng !== null && l.boq_quantity !== '' && l.boq_quantity != null ? eng - num(l.boq_quantity) : null;
                    const manual = !l.boq_item_id;
                    return (
                      <React.Fragment key={l._key}>
                        <tr style={{ background: 'rgba(11,49,139,0.05)' }}>
                          <td style={{ ...td, textAlign: 'center', cursor: 'pointer' }} onClick={() => setExpanded(x => ({ ...x, [l._key]: !open }))}>
                            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                          </td>
                          <td style={{ ...td, fontFamily: 'monospace', fontWeight: 800, color: NAVY }}>
                            {editable && manual ? <Cell value={l.boq_code} onChange={v => updLine(active._key, l._key, { boq_code: v })} width={70} placeholder="Code" /> : (l.boq_code || '—')}
                          </td>
                          <td style={td}>
                            {editable && manual
                              ? <Cell value={l.boq_description} onChange={v => updLine(active._key, l._key, { boq_description: v })} width="100%" placeholder="BOQ description *" />
                              : <span style={{ fontWeight: 700 }}>{l.boq_description}</span>}
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                              {l.materials.length} material{l.materials.length === 1 ? '' : 's'}{manual ? ' · not on the BOQ' : ''}
                            </div>
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                            {editable && manual ? <Cell type="number" value={l.boq_quantity} onChange={v => updLine(active._key, l._key, { boq_quantity: v })} width={80} align="right" /> : fmt(l.boq_quantity)}
                          </td>
                          <td style={td}>{editable && manual ? <Cell value={l.boq_uom} onChange={v => updLine(active._key, l._key, { boq_uom: v })} width={60} /> : (l.boq_uom || '—')}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmt(l.total_transferred)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{l.boq_quantity === '' || l.boq_quantity == null ? '—' : fmt(num(l.boq_quantity) - num(l.total_transferred))}</td>
                          <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap', color: variance == null ? 'var(--text-secondary)' : variance > 0 ? '#dc2626' : '#16a34a' }}>
                            {variance == null ? '—' : `${variance > 0 ? '+' : ''}${fmt(variance)} ${l.boq_uom || ''}${num(l.boq_quantity) ? ` (${fmt((variance / num(l.boq_quantity)) * 100, 1)}%)` : ''}`}
                          </td>
                          <td style={td}>
                            {editable ? (
                              <select value={l.line_type} onChange={e => updLine(active._key, l._key, { line_type: e.target.value })}
                                      style={{ padding: '5px 6px', borderRadius: 7, border: '1px solid var(--border-light)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 12 }}>
                                {LINE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            ) : LINE_TYPES.find(o => o.value === l.line_type)?.label}
                          </td>
                          <td style={td}><LineStatusPill status={st} /></td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            {editable && (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <label title="Needs clarification" style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={!!l.needs_clarification} onChange={e => updLine(active._key, l._key, { needs_clarification: e.target.checked })} /> Clarify
                                </label>
                                {manual && <button title="Remove this line" onClick={() => removeLine(active._key, l._key)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', display: 'flex' }}><Trash2 size={14} /></button>}
                              </div>
                            )}
                          </td>
                        </tr>

                        {open && (
                          <tr>
                            <td colSpan={11} style={{ padding: '8px 10px 12px 36px', border: '1px solid var(--border-light)', background: 'var(--bg-card)' }}>
                              {l.line_type === 'lot' && !l.materials.length && (
                                <div style={{ fontSize: 12, color: '#3730a3', marginBottom: 8 }}>↓ Lot item — break it down into its child materials.</div>
                              )}
                              {l.materials.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                    <thead>
                                      <tr>
                                        {['#', 'Material (Master)', 'Description', 'Specification', 'Brand', 'Req Qty', 'Meas UOM', 'Conv', 'Purch UOM', 'Add %', 'Total Qty', 'Proc Qty', 'Remarks', ''].map(h => (
                                          <th key={h} style={{ ...th, background: '#334155', fontSize: 9 }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {l.materials.map((m, i) => {
                                        const q = materialQuantities(m);
                                        const isNew = m.is_new_material && !m.material_id;
                                        return (
                                          <tr key={m._key}>
                                            <td style={{ ...td, color: 'var(--text-secondary)' }}>{String(i + 1).padStart(2, '0')}</td>
                                            <td style={{ ...td, minWidth: 200 }}>
                                              {editable ? (
                                                <SearchableSelect value={isNew ? NEW_MATERIAL : (m.material_id || '')} onChange={v => pickMaterial(active._key, l._key, m._key, v)}
                                                                  options={materialOptions} placeholder="— Pick from master —" dropdownWidth={360} />
                                              ) : (m.material_code || (isNew ? 'New material' : '—'))}
                                              {isNew && <div style={{ fontSize: 10.5, color: '#9d174d', marginTop: 3 }}>Needs Material Master registration</div>}
                                            </td>
                                            <td style={td}>{editable ? <Cell value={m.description} onChange={v => updMat(active._key, l._key, m._key, { description: v })} width={200} placeholder="Description *" /> : <b>{m.description}</b>}</td>
                                            <td style={td}>{editable ? <Cell value={m.specification} onChange={v => updMat(active._key, l._key, m._key, { specification: v })} width={130} /> : (m.specification || '—')}</td>
                                            <td style={td}>{editable ? <Cell value={m.brand} onChange={v => updMat(active._key, l._key, m._key, { brand: v })} width={100} /> : (m.brand || '—')}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{editable ? <Cell type="number" value={m.required_quantity} onChange={v => updMat(active._key, l._key, m._key, { required_quantity: v })} width={80} align="right" /> : fmt(m.required_quantity)}</td>
                                            <td style={td}>{editable ? <Cell value={m.measured_uom} onChange={v => updMat(active._key, l._key, m._key, { measured_uom: v })} width={60} /> : (m.measured_uom || '—')}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{editable ? <Cell type="number" value={m.conversion_factor} onChange={v => updMat(active._key, l._key, m._key, { conversion_factor: v })} width={60} align="right" /> : fmt(m.conversion_factor)}</td>
                                            <td style={td}>{editable ? <Cell value={m.purchase_uom} onChange={v => updMat(active._key, l._key, m._key, { purchase_uom: v })} width={70} /> : (m.purchase_uom || '—')}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{editable ? <Cell type="number" value={m.additional_percentage} onChange={v => updMat(active._key, l._key, m._key, { additional_percentage: v })} width={55} align="right" /> : `${fmt(m.additional_percentage)}%`}</td>
                                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(q.total)} <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>{m.measured_uom}</span></td>
                                            <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: NAVY, whiteSpace: 'nowrap' }}>{fmt(q.procurement, 0)} <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>{m.purchase_uom || m.measured_uom}</span></td>
                                            <td style={td}>{editable ? <Cell value={m.remarks} onChange={v => updMat(active._key, l._key, m._key, { remarks: v })} width={110} /> : (m.remarks || '—')}</td>
                                            <td style={td}>
                                              {editable && <button title="Remove material" onClick={() => removeMat(active._key, l._key, m._key)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', display: 'flex' }}><Trash2 size={14} /></button>}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {!l.materials.length && !editable && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Not broken down.</div>}
                              {editable && (
                                <button onClick={() => addMat(active._key, l._key)} style={{ marginTop: 8, background: 'none', border: '1px dashed var(--border-light)', borderRadius: 8, padding: '6px 12px', color: NAVY, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <Plus size={13} /> {l.line_type === 'lot' ? 'Add Sub-Line' : 'Add Material'}
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      {/* ── traceability ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <Panel title="Approval history" style={{ marginBottom: 0 }}>
          {!tracking ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{neverSubmitted ? 'Not submitted yet — still a draft.' : 'Loading…'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12.5 }}>Submitted by <b>{tracking.requester_details?.full_name || takeoff.submitted_by_name || '—'}</b> on {fmtDate(takeoff.submitted_at)}</div>
              {(tracking.approval_history || []).map(h => (
                <div key={`${h.step_id}-${h.date_actioned}`} style={{ border: '1px solid var(--border-light)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <b>{h.step_name}</b>
                    <span style={{ color: h.status === 'approved' ? '#16a34a' : h.status === 'returned' ? '#ea580c' : h.status === 'rejected' ? '#dc2626' : 'var(--text-secondary)', fontWeight: 700, textTransform: 'capitalize' }}>{h.status || h.action || 'pending'}</span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{h.approver_fullname}{h.date_actioned ? ` · ${fmtDate(h.date_actioned)}` : ''}</div>
                  {h.comments && <div style={{ marginTop: 4 }}>“{h.comments}”</div>}
                </div>
              ))}
              {tracking.current_step && takeoff.status === 'pending_approval' && (
                <div style={{ fontSize: 12.5, color: '#854d0e' }}>Now at <b>{tracking.current_step.step_name}</b>{tracking.current_step.assigned_to?.full_name ? ` — ${tracking.current_step.assigned_to.full_name}` : ''}</div>
              )}
            </div>
          )}
        </Panel>

        <Panel title="Revisions & material requests" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {takeoff.revisions.map(r => (
              <button key={r.id} onClick={() => r.id !== takeoff.id && onOpenTakeoff(r.id)} disabled={r.id === takeoff.id}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, fontFamily: 'var(--font)',
                               border: `1px solid ${r.id === takeoff.id ? NAVY : 'var(--border-light)'}`, background: 'var(--bg-base)', color: 'var(--text-primary)', cursor: r.id === takeoff.id ? 'default' : 'pointer' }}>
                <span style={{ fontWeight: 800 }}><GitBranch size={13} style={{ verticalAlign: -2 }} /> {r.revision}{r.id === takeoff.id ? ' (this one)' : ''}</span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>{fmtDate(r.date_prepared)} <TakeoffStatusPill status={r.status} /></span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 6 }}>MATERIAL REQUESTS RAISED</div>
          {takeoff.material_request_ids.length === 0
            ? <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>None yet{takeoff.status === 'approved' ? ' — use Raise Material Request.' : '.'}</div>
            : takeoff.material_request_ids.map((id, i) => (
              <button key={id} onClick={() => onOpenMR(id)} style={{ display: 'block', background: 'none', border: 'none', color: NAVY, fontWeight: 700, cursor: 'pointer', padding: '3px 0', fontSize: 13 }}>
                <FileOutput size={13} style={{ verticalAlign: -2 }} /> Material Request #{i + 1}
              </button>
            ))}
        </Panel>
      </div>

      {/* ── action bar ── */}
      <div style={{ position: 'sticky', bottom: 0, zIndex: 5, marginTop: 16, background: 'var(--bg-card)', border: '2px solid black', borderRadius: 12, padding: '10px 14px', display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', boxShadow: '0 -6px 18px rgba(0,0,0,0.08)' }}>
        {editable && neverSubmitted && takeoff.status === 'draft' && (
          <Btn variant="danger" onClick={() => setModal('delete')} disabled={!!busy} style={{ marginRight: 'auto' }}><Trash2 size={14} /> Delete Draft</Btn>
        )}
        {editable && (
          <>
            <Btn onClick={() => save()} loading={busy === 'save'} disabled={!!busy || !dirty}
                 style={{ background: '#16a34a', border: '1px solid #16a34a', color: '#fff', boxShadow: 'none' }}><Save size={14} /> Save Draft</Btn>
            <Btn onClick={() => setModal('submit')} disabled={!!busy || stats.mats === 0} title={stats.mats === 0 ? 'Break at least one BOQ line into materials first' : undefined}>
              <Send size={14} /> {takeoff.status === 'returned' ? 'Resubmit for Approval' : 'Submit for Approval'}
            </Btn>
          </>
        )}
        {takeoff.status === 'pending_approval' && isPreparer && (
          <Btn variant="secondary" onClick={() => run('recall', () => api.recallTakeoff(takeoff.id))} loading={busy === 'recall'} disabled={!!busy}><Undo2 size={14} /> Recall</Btn>
        )}
        {takeoff.status === 'pending_approval' && canAct && (
          <>
            <Btn variant="danger" onClick={() => setModal('reject')} disabled={!!busy}><XCircle size={14} /> Reject</Btn>
            <Btn variant="secondary" onClick={() => setModal('return')} disabled={!!busy} style={{ color: '#c2410c' }}><RotateCcw size={14} /> Return</Btn>
            <Btn onClick={() => setModal('approve')} disabled={!!busy} style={{ background: '#16a34a', border: '1px solid #16a34a', color: '#fff', boxShadow: 'none' }}><CheckCircle2 size={14} /> Approve</Btn>
          </>
        )}
        {takeoff.status === 'approved' && (
          <>
            <Btn variant="secondary" onClick={() => setModal('revise')} disabled={!!busy}><GitBranch size={14} /> Revise (new revision)</Btn>
            <Btn onClick={() => setModal('mr')} disabled={!!busy || linesWithBalance.length === 0}
                 title={linesWithBalance.length === 0 ? 'Every BOQ line has been transferred already' : undefined}><FileOutput size={14} /> Raise Material Request</Btn>
          </>
        )}
        {!editable && takeoff.status !== 'approved' && !(takeoff.status === 'pending_approval' && (canAct || isPreparer)) && (
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', alignSelf: 'center' }}>
            {EDITABLE.includes(takeoff.status) ? 'Only the engineer who prepared this take-off can edit it.' : 'Read-only.'}
          </span>
        )}
      </div>

      {/* ── modals ── */}
      {modal === 'system' && (
        <AddSystemModal takeoff={takeoff} existing={systems.map(s => s.system_name.toLowerCase())}
                        onClose={() => setModal(null)}
                        onAdd={async (payload) => {
                          if (dirty && !(await save({ quiet: true }))) return;
                          await run('system', () => api.addTakeoffSystem(takeoff.id, payload), (t) => {
                            adopt(t);
                            const added = t.systems[t.systems.length - 1];
                            if (added) { setActiveKey(added.id); setExpanded(Object.fromEntries(added.boq_lines.map(l => [l.id, true]))); }
                            toast.show(`${payload.system_name} added${added?.boq_lines.length ? ` with ${added.boq_lines.length} BOQ line(s)` : ''}.`, 'success');
                          });
                        }} />
      )}
      {modal === 'submit' && (
        <CommentModal title={takeoff.status === 'returned' ? 'Resubmit take-off' : 'Submit take-off for approval'}
                      message={`${takeoff.takeoff_ref} ${takeoff.revision} goes to the approval workflow${dirty ? ' (your unsaved changes are saved first)' : ''}. ${stats.mats} material line(s) across ${stats.lines} BOQ line(s).`}
                      label="Comment (optional)" confirmLabel={takeoff.status === 'returned' ? 'Resubmit' : 'Submit'}
                      onConfirm={submit} onClose={() => setModal(null)} />
      )}
      {modal === 'approve' && (
        <CommentModal title="Approve take-off" message={`Approve ${takeoff.takeoff_ref} ${takeoff.revision} at your step.`} label="Comment (optional)" confirmLabel="Approve"
                      onConfirm={(c) => run('approve', () => api.approveTakeoff(takeoff.id, c))} onClose={() => setModal(null)} />
      )}
      {modal === 'return' && (
        <CommentModal title="Return for correction" message="The engineer gets the take-off back to correct and resubmit." label="What needs correcting" required confirmLabel="Return"
                      onConfirm={(c) => run('return', () => api.returnTakeoff(takeoff.id, c))} onClose={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <CommentModal title="Reject take-off" message="Rejection ends this take-off's approval; it cannot be resubmitted." label="Reason" required danger confirmLabel="Reject"
                      onConfirm={(c) => run('reject', () => api.rejectTakeoff(takeoff.id, c))} onClose={() => setModal(null)} />
      )}
      {modal === 'revise' && (
        <CommentModal title={`Revise ${takeoff.takeoff_ref}`} label="Reason for revision" required confirmLabel="Create revision"
                      message={`A new draft revision is created from ${takeoff.revision}. ${takeoff.revision} stays in force until the new revision is approved; it is then superseded.`}
                      onConfirm={(reason) => run('revise', () => api.reviseTakeoff(takeoff.id, reason), (t) => { toast.show(`${t.takeoff_ref} ${t.revision} created.`, 'success'); onOpenTakeoff(t.id); })}
                      onClose={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <Modal title="Delete draft" onClose={() => setModal(null)} width={460} blur={2}>
          <p style={{ fontSize: 14, margin: '4px 2px 18px' }}>Delete <b>{takeoff.takeoff_ref}</b> and its whole breakdown? This cannot be undone.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn loading={busy === 'delete'} style={{ background: '#dc2626', border: '1px solid #dc2626', color: '#fff', boxShadow: 'none' }}
                 onClick={() => run('delete', () => api.deleteTakeoff(takeoff.id), () => { toast.show(`${takeoff.takeoff_ref} deleted.`, 'success'); onBack(); })}>Delete</Btn>
          </div>
        </Modal>
      )}
      {modal === 'leave' && (
        <Modal title="Unsaved changes" onClose={() => setModal(null)} width={460} blur={2}>
          <p style={{ fontSize: 14, margin: '4px 2px 18px' }}>You have changes that are not saved. Leave and lose them?</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={() => setModal(null)}>Stay</Btn>
            <Btn style={{ background: '#dc2626', border: '1px solid #dc2626', color: '#fff', boxShadow: 'none' }} onClick={() => { setDirty(false); onBack(); }}>Leave without saving</Btn>
          </div>
        </Modal>
      )}
      {modal === 'mr' && (
        <RaiseMRModal takeoff={takeoff} lines={linesWithBalance} busy={busy === 'mr'} onClose={() => setModal(null)}
                      onRaise={(payload) => run('mr', () => api.raiseMRFromTakeoff(takeoff.id, payload), async (r) => {
                        toast.show(r.message, 'success');
                        await load();
                        onOpenMR(r.material_request_id);
                      })} />
      )}
    </div>
  );
}

function AddSystemModal({ takeoff, existing, onAdd, onClose }) {
  const [name, setName] = useState('');
  const [custom, setCustom] = useState('');
  const [boqId, setBoqId] = useState('');
  const [boqs, setBoqs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.getTakeoffBoqOptions(takeoff.project_id, takeoff.project_division_id || undefined).then(r => setBoqs(r || [])).catch(() => {});
  }, [takeoff.project_id, takeoff.project_division_id]);
  const finalName = (name === 'Other' ? custom : name).trim();
  const clash = finalName && existing.includes(finalName.toLowerCase());
  const go = async () => { setBusy(true); try { await onAdd({ system_name: finalName, boq_id: boqId || null }); } finally { setBusy(false); } };
  return (
    <Modal title="Add system" onClose={onClose} width={520} blur={2}>
      <p style={{ margin: '2px 2px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>The system's BOQ lines load into the working area.</p>
      <div style={{ display: 'grid', gap: 12 }}>
        <Select label="System *" title="Select System" value={name} onChange={setName} options={SYSTEM_SUGGESTIONS.map(s => ({ value: s, label: s }))} />
        {name === 'Other' && <Input label="System name *" value={custom} onChange={setCustom} placeholder="e.g. Irrigation" />}
        <Select label="BOQ" title="Select BOQ" value={boqId} onChange={setBoqId}
                options={[{ value: '', label: '— None (add lines by hand) —' }, ...boqs.map(b => ({ value: b.id, label: `${b.name} · ${b.item_count} item${b.item_count === 1 ? '' : 's'}` }))]} />
        {clash && <div style={{ fontSize: 12.5, color: '#dc2626' }}>{finalName} is already on this take-off.</div>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={go} loading={busy} disabled={!finalName || clash}><Plus size={14} /> Add System</Btn>
      </div>
    </Modal>
  );
}

function RaiseMRModal({ takeoff, lines, busy, onRaise, onClose }) {
  const [picked, setPicked] = useState(() => Object.fromEntries(lines.map(l => [l.id, true])));
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  const [submit, setSubmit] = useState(false);
  const chosen = lines.filter(l => picked[l.id]);
  const all = chosen.length === lines.length;
  return (
    <Modal title="Raise Material Request" onClose={onClose} width={760} blur={2}>
      <p style={{ margin: '2px 2px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
        Each system becomes a category, each BOQ line a line item and each material a sub-item. The BOQ balance of the chosen
        lines moves to the request, so a line is never requested twice.
      </p>
      <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid black', borderRadius: 8, marginBottom: 14 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}><input type="checkbox" checked={all} onChange={e => setPicked(Object.fromEntries(lines.map(l => [l.id, e.target.checked])))} /></th>
              <th style={th}>System</th><th style={th}>BOQ</th><th style={{ ...th, textAlign: 'right' }}>Balance</th><th style={{ ...th, textAlign: 'right' }}>Materials</th>
            </tr>
          </thead>
          <tbody>
            {lines.map(l => (
              <tr key={l.id}>
                <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={!!picked[l.id]} onChange={e => setPicked(p => ({ ...p, [l.id]: e.target.checked }))} /></td>
                <td style={td}>{l.system_name}</td>
                <td style={td}><b>{l.boq_code}</b> {l.boq_description}</td>
                <td style={{ ...td, textAlign: 'right' }}>{l.boq_quantity == null ? '—' : `${fmt(l.balance)} ${l.boq_uom || ''}`}</td>
                <td style={{ ...td, textAlign: 'right' }}>{l.materials.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>
        <Select label="Priority" title="Priority" value={priority} onChange={setPriority}
                options={[{ value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' }]} />
        <Textarea label="Notes" value={notes} onChange={setNotes} rows={2} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={submit} onChange={e => setSubmit(e.target.checked)} />
        Submit it for Cost Control review straight away (otherwise it is saved as a draft request)
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn loading={busy} disabled={!chosen.length}
             onClick={() => onRaise({ boq_line_ids: all ? null : chosen.map(l => l.id), priority, notes: notes || null, submit })}>
          <FileOutput size={14} /> Raise for {chosen.length} BOQ line{chosen.length === 1 ? '' : 's'}
        </Btn>
      </div>
      <p style={{ margin: '10px 2px 0', fontSize: 11.5, color: 'var(--text-secondary)' }}>From {takeoff.takeoff_ref} {takeoff.revision}.</p>
    </Modal>
  );
}
